import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Globe } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';

export const Navbar = ({ onBookClick }) => {
  const { language, toggleLanguage, t, isRTL } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  const navItems = [
    { id: 'home', label: t.nav.home },
    { id: 'about', label: t.nav.about },
    { id: 'services', label: t.nav.services },
    { id: 'reviews', label: t.nav.reviews },
    { id: 'gallery', label: t.nav.gallery },
    { id: 'contact', label: t.nav.contact },
  ];

  return (
    <>
      <motion.nav
        data-testid="navbar"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'nav-glass nav-scrolled' : 'bg-transparent'
        }`}
      >
        <div className="container mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <motion.div
              whileHover={{ scale: 1.02 }}
              className="cursor-pointer"
              onClick={() => scrollToSection('home')}
            >
              <h1 
                data-testid="logo"
                className={`text-2xl md:text-3xl font-bold heading ${
                  isScrolled ? 'text-[#4A4A4A]' : 'text-[#C9A050]'
                }`}
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                {language === 'ar' ? 'سيرينا' : 'Sirena'}
              </h1>
            </motion.div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  data-testid={`nav-${item.id}`}
                  onClick={() => scrollToSection(item.id)}
                  className={`text-sm font-medium transition-colors hover:text-[#D4A39A] ${
                    isScrolled ? 'text-[#4A4A4A]' : 'text-white'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>

            {/* Actions */}
            <div className="flex items-center gap-4">
              {/* Language Toggle */}
              <Button
                data-testid="language-toggle"
                variant="ghost"
                size="icon"
                onClick={toggleLanguage}
                className={`rounded-full ${
                  isScrolled ? 'text-[#4A4A4A] hover:bg-[#F9F7F2]' : 'text-white hover:bg-white/20'
                }`}
              >
                <Globe className="w-5 h-5" />
                <span className="sr-only">
                  {language === 'en' ? 'Switch to Arabic' : 'Switch to English'}
                </span>
              </Button>
              <span 
                className={`hidden sm:inline text-sm font-medium ${
                  isScrolled ? 'text-[#4A4A4A]' : 'text-white'
                }`}
              >
                {language === 'en' ? 'عربي' : 'EN'}
              </span>

              {/* Book Now Button */}
              <Button
                data-testid="nav-book-btn"
                onClick={onBookClick}
                className="hidden md:flex btn-primary text-sm"
              >
                {t.nav.bookNow}
              </Button>

              {/* Mobile Menu Button */}
              <Button
                data-testid="mobile-menu-btn"
                variant="ghost"
                size="icon"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className={`lg:hidden rounded-full ${
                  isScrolled ? 'text-[#4A4A4A]' : 'text-white'
                }`}
              >
                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </Button>
            </div>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed inset-0 z-40 bg-white pt-24 px-6"
          >
            <div className="flex flex-col gap-6">
              {navItems.map((item, index) => (
                <motion.button
                  key={item.id}
                  data-testid={`mobile-nav-${item.id}`}
                  initial={{ opacity: 0, x: isRTL ? 20 : -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  onClick={() => scrollToSection(item.id)}
                  className="text-xl font-medium text-[#4A4A4A] hover:text-[#D4A39A] transition-colors text-start"
                >
                  {item.label}
                </motion.button>
              ))}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
              >
                <Button
                  data-testid="mobile-book-btn"
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    onBookClick();
                  }}
                  className="w-full btn-primary mt-4"
                >
                  {t.nav.bookNow}
                </Button>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
